import {AfterViewInit, Component, inject, OnDestroy, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MediaService} from "../../../services/media/media.service";
import {NgbModal, NgbModalOptions, NgbModalRef, NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {DomSanitizer, SafeUrl} from "@angular/platform-browser";
import {of, Subject, Subscription} from "rxjs";
import {isBlank, isDefined, isFalse, isTrue, isUuid, nonBlank, notDefined, unsubscribe} from "../../utils/epic.utils";
import {ButtonComponent} from "../../button/button.component";
import {ActionBarComponent} from "../../action-bar/action-bar.component";
import {CardComponent} from "../../card/card.component";
import {CardBodyComponent} from "../../card/card-body/card-body.component";
import {TranslatePipe} from "@ngx-translate/core";
import {saveAs} from 'file-saver';
import {HttpResponse} from "@angular/common/http";
import {LoggingService} from "../../../services/logger/logging.service";
import {MediaDto} from "../../../repositories/dto/media.dto";
import {InvoiceDto} from "../../../repositories/dto/document/invoice.dto";
import {MediaViewerService} from "../../../services/media/media-viewer.service";
import {IconComponent} from "../../icon/icon.component";
import {PermissionService} from "../../../services/auth/permission.service";
import {Permissions} from "../../../services/auth/permissions.item";
import {MediaFileType} from "../../enum/media-file-type.enum";
import {filter} from "rxjs/operators";
import {InvoiceService} from "../../../services/invoice/invoice.service";
import {SubInvoiceDto} from "../../../repositories/dto/sub-invoice.dto";
import {SpinnerComponent} from "../../spinner/spinner.component";
import {LetterSendViewerComponent} from "../../viewer/letter-send-viewer/letter-send-viewer.component";
import {EmailSendViewerComponent} from "../../viewer/email-send-viewer/email-send-viewer.component";
import {MediaType} from "../media.type";
import {FormWrapper} from "../../form/items/form.wrapper";
import {FormService} from "../../../services/form.service";
import {Belong} from "../../enum/belong.enum";
import {PermissionGrant} from "../../../services/auth/permission-grant.enum";
import {FormMetadata} from "../../form/items/form-metadata";
import {FormInputConfig} from "../../form/form-input/form-input-config";

@Component({
  selector: 'acc-media-viewer',
  imports: [
    ButtonComponent,
    ActionBarComponent,
    CardComponent,
    CardBodyComponent,
    TranslatePipe,
    IconComponent,
    NgbTooltip,
    LetterSendViewerComponent,
    EmailSendViewerComponent,
    SpinnerComponent
  ],
  templateUrl: './media-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './media-viewer.component.css'
})
export class MediaViewerComponent implements OnDestroy, AfterViewInit {

  @ViewChild('mediaTpl')
  protected mediaTpl: TemplateRef<any> | undefined;

  @ViewChild('letterSendViewer')
  protected letterSendViewer: LetterSendViewerComponent | undefined;

  @ViewChild('emailSendViewer')
  protected emailSendViewer: EmailSendViewerComponent | undefined;

  protected readonly PermissionGrant: typeof PermissionGrant = PermissionGrant;
  protected readonly inputConfig: FormInputConfig = new FormInputConfig();

  protected mediaUrl: SafeUrl | undefined;
  protected imageFailed: boolean = false;
  protected formattedXml: string | undefined;
  protected formattedCsv: string | undefined;
  protected contentType: string | undefined;
  protected invoice: InvoiceDto | undefined;
  protected subInvoice: SubInvoiceDto | undefined;
  protected perms: Permissions;
  protected showLetterSendViewer: boolean = false;
  protected showEmailSendViewer: boolean = false;

  private readonly modalService: NgbModal = inject(NgbModal);
  private readonly mediaService: MediaService = inject(MediaService);
  private readonly sanitizer: DomSanitizer = inject(DomSanitizer);
  private readonly log: LoggingService = inject(LoggingService);
  private readonly mediaViewerService: MediaViewerService = inject(MediaViewerService);
  private readonly permissionService: PermissionService = inject(PermissionService);
  private readonly invoiceService: InvoiceService = inject(InvoiceService);
  private readonly formService: FormService = inject(FormService);

  private readonly subs: Subscription[] = [];
  private readonly loadDocumentContactType: MediaFileType[] = [MediaFileType.INVOICE, MediaFileType.CANCELLATION, MediaFileType.COST_ESTIMATE];
  private readonly tagsType: MediaFileType[] = []; // [MediaFileType.UPLOAD];
  private readonly deletableType: MediaFileType[] = []; // [MediaFileType.UPLOAD];
  private readonly mediaLoaded: Subject<boolean> = new Subject<boolean>();
  private readonly options: NgbModalOptions = {
    centered: true,
    keyboard: true,
    windowClass: 'pdf-viewer media-viewer modal-fullscreen'
  };
  private opened: boolean = false;
  private preview: boolean = false;
  private mediaId: string | undefined = undefined;
  private modalRef: NgbModalRef | undefined;
  private mediaRes: HttpResponse<Blob> | undefined = undefined;
  private supportedContentTypes: string[] = [MediaType.PDF, MediaType.PNG, MediaType.JPG, MediaType.JPEG, MediaType.CSV, MediaType.TIF, MediaType.TIFF];
  private mediaFileType: MediaFileType | undefined;
  private mediaBelong: Belong | undefined;
  private mediaTags: string | undefined;
  private mediaDeletableAfter: Date | undefined;
  private tagsWrapper: FormWrapper | undefined;

  constructor() {
    this.perms = this.permissionService.getPerms();
    this.permissionService.accChanged().subscribe((p) => this.perms = p);
    this.mediaLoaded.pipe(filter((b) => b)).subscribe(() => this.onMediaLoaded());
  }

  ngAfterViewInit(): void {
    this.mediaViewerService.setMediaViewer(this);
  }

  ngOnDestroy(): void {
    this.close();
    this.clean();
    unsubscribe(this.subs);
    this.formService.destroyWrapper(this.tagsWrapper);
    this.tagsWrapper = undefined;
  }

  public isContentTypeSupported(contentType: string): boolean {
    return this.supportedContentTypes.includes(contentType);
  }

  /*
   * Chrome can be set to download PDFs instead of opening them ("PDFs herunterladen",
   * or the AlwaysOpenPdfExternally policy). The built-in viewer is then gone and the
   * iframe stays empty - no matter whether it is fed a data:, blob: or http: source.
   * The property is undefined in browsers that do not know it, so only an explicit
   * false counts as switched off.
   */
  protected isPdfViewerDisabled(): boolean {
    return isFalse(navigator.pdfViewerEnabled);
  }

  public openInvoicePreview(invoiceId: string): Promise<boolean> {
    this.loadInvoicePreviewContent(invoiceId);
    this.preview = true;
    return this.modalOpen();
  }

  public isOpen(): boolean {
    return this.opened;
  }

  public isPreview(): boolean {
    return this.preview;
  }

  public close(): void {
    this.modalRef?.close();
    this.opened = false;
    this.invoice = undefined;
    this.subInvoice = undefined;
    this.showLetterSendViewer = false;
    this.showEmailSendViewer = false;
  }

  public open(media: string | MediaDto, invoice?: InvoiceDto, subInvoice?: SubInvoiceDto): Promise<boolean> {
    const mediaId: string = media instanceof MediaDto ? media.id : media;
    // Set Invoice
    this.invoice = undefined;
    if (isDefined(invoice) && isUuid(invoice.mediaId) && mediaId === invoice.mediaId) {
      this.invoice = invoice.clone();
      this.showLetterSendViewer = nonBlank(invoice.contact.letterAddress) && isTrue(invoice.contact.completeAsLetter);
      this.showEmailSendViewer = isUuid(invoice.contact.contactId);
    }
    // Set DeliveryNote and ProformaInvoice
    this.subInvoice = undefined;
    if (isDefined(invoice) && isDefined(subInvoice) && isUuid(subInvoice.mediaId) && mediaId === subInvoice.mediaId) {
      this.invoice = invoice.clone();
      this.subInvoice = subInvoice.clone();
      this.showLetterSendViewer = false;
      this.showEmailSendViewer = isUuid(subInvoice.contact?.contactId);
    }
    this.preview = false;
    // Called at the end so that variables are set before a process starts
    return this.setMediaId(mediaId);
  }

  public openForResponse(res: HttpResponse<Blob>): Promise<boolean> {
    this.mediaRes = res;

    if (isDefined(res.body) && isDefined(res.body.type)) {
      this.readHeaders(res);

      const reader = new FileReader();
      reader.readAsDataURL(res.body);
      reader.onloadend = (data) => {
        this.mediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(String(data.target!.result));
        this.mediaLoaded.next(true);
      };
      return this.modalOpen();
    } else if (notDefined(res.body)) {
      this.log.error("MediaViewer: response missing body");
    } else if (notDefined(res.body.type)) {
      this.log.error("MediaViewer: response missing body.type");
    }
    return Promise.reject(false);
  }

  protected isTagsType(): boolean {
    return isDefined(this.mediaFileType) && isDefined(this.mediaBelong) && this.tagsType.includes(this.mediaFileType);
  }

  protected isDeletableType(): boolean {
    return isDefined(this.mediaFileType) && isDefined(this.mediaBelong) && this.deletableType.includes(this.mediaFileType);
  }

  protected isDeletable(): boolean {
    return true;
  }

  protected delete(): void {
    console.log("TODO delete")
  }

  protected isDownloadAllowed(): boolean {
    return !this.isPreview() && isDefined(this.mediaUrl) && isDefined(this.mediaRes) && isDefined(this.mediaRes.body);
  }

  protected download(): void {
    if (this.isDownloadAllowed()) {
      const filename: string = this.mediaService.parseContentDisposition(this.mediaRes!.headers.get('content-disposition'), this.contentType);
      saveAs(this.mediaRes!.body!, filename);
    }
  }

  protected openLetterPriceCalc(): void {
    if (isDefined(this.subInvoice)) {
      this.letterSendViewer?.openSubInvoice(this.subInvoice);
    } else if (isDefined(this.invoice)) {
      this.letterSendViewer?.openInvoice(this.invoice);
    }
  }

  protected openEmailSendViewer(): void {
    if (isDefined(this.subInvoice)) {
      this.emailSendViewer?.openSubInvoice(this.subInvoice);
    } else if (isDefined(this.invoice)) {
      this.emailSendViewer?.openInvoice(this.invoice);
    }
  }

  protected hasPermission(grant: PermissionGrant): boolean {
    if (isDefined(this.mediaBelong)) {
      switch (this.mediaBelong) {
        case Belong.INVOICE:
          return this.perms.invoice.isAllow(grant);
        case Belong.CONTACT:
          return this.perms.contact.isAllow(grant);
        case Belong.CONTACT_VEHICLE:
          return this.perms.contactVehicle.isAllow(grant);
        case Belong.ARTICLE:
          return this.perms.article.isAllow(grant);
        case Belong.DEATH:
          return this.perms.funeral.isAllow(grant);
        case Belong.RECEIPT:
          return this.perms.receipt.isAllow(grant);
        default:
          this.log.error('Media Belong "{}" is not supported in MediaViewer', [this.mediaBelong]);
      }
    }
    return false;
  }

  private onMediaLoaded(): void {
    // Load DocumentContact if not available, e.g., viewer was called from Invoice-List
    if (isDefined(this.invoice) && isFalse(this.showLetterSendViewer)
      && isBlank(this.invoice.contact.letterAddress) && isUuid(this.invoice.contact.id)
      && isDefined(this.mediaFileType) && this.loadDocumentContactType.includes(this.mediaFileType)) {
      this.subs.push(this.invoiceService.getContactById(this.invoice.id, this.invoice.contact.id).subscribe((c) => {
        if (isDefined(this.invoice)) {
          this.invoice.contact = c;
          this.showLetterSendViewer = nonBlank(c.letterAddress) && isTrue(c.completeAsLetter);
          this.showEmailSendViewer = isUuid(c.contactId);
        }
      }));
    } else if (isDefined(this.invoice) && isDefined(this.subInvoice) && isUuid(this.subInvoice.id) && isFalse(this.showLetterSendViewer)
      && isDefined(this.mediaFileType) && [MediaFileType.DELIVERY_NOTE, MediaFileType.PROFORMA_INVOICE].includes(this.mediaFileType)) {
      this.subs.push(this.invoiceService.getSubInvoiceContact(this.invoice.id, this.subInvoice).subscribe((c) => {
        if (isDefined(this.subInvoice)) {
          this.subInvoice.contact = c;
          this.showLetterSendViewer = nonBlank(c.letterAddress) && isTrue(c.completeAsLetter);
          this.showEmailSendViewer = isUuid(c.contactId);
        }
      }));
    }
  }

  private async modalOpen(): Promise<boolean> {
    // Idempotent: the preview flow calls this twice (openInvoicePreview + openForResponse).
    // Reuse the open modal so it transitions spinner -> content instead of stacking a second,
    // orphaned modal whose ref gets overwritten and can no longer be closed.
    if (this.opened && isDefined(this.modalRef)) {
      return true;
    }
    this.modalRef = this.modalService.open(this.mediaTpl, this.options);
    this.opened = true;
    // Keep 'opened' in sync when the user dismisses via Esc/backdrop (result rejects) or closes (resolves)
    this.modalRef.result.then(() => this.opened = false, () => this.opened = false);
    return true;
  }

  private clean(): void {
    this.mediaRes = undefined;
    this.mediaUrl = undefined;
    this.imageFailed = false;
    this.mediaFileType = undefined;
    this.mediaBelong = undefined;
    this.mediaTags = undefined;
    this.mediaDeletableAfter = undefined;
    this.mediaLoaded.next(false);
    this.formService.destroyWrapper(this.tagsWrapper);
    this.tagsWrapper = undefined;
  }

  private setMediaId(mediaId: string): Promise<boolean> {
    if (isUuid(mediaId)) {
      if (this.mediaId !== mediaId) {
        this.mediaId = mediaId;
        if (this.isOpen()) {
          this.close();
        }
        this.loadMediaContent(this.mediaId);
      } else {
        this.log.trace('Media ID is already loaded "{}"', [mediaId]);
        this.mediaLoaded.next(true);
      }
      return this.modalOpen();
    } else {
      this.log.error('Media ID is invalid "{}"', [mediaId]);
    }
    return Promise.resolve(this.opened);
  }

  private loadInvoicePreviewContent(invoiceId: string): void {
    this.clean();
    unsubscribe(this.subs);
    this.subs.push(this.invoiceService.createPreview(invoiceId).subscribe((res) => {
      this.openForResponse(res).then();
    }));
  }

  private loadMediaContent(mediaId: string): void {
    this.clean();
    unsubscribe(this.subs);
    this.subs.push(this.mediaService.loadContent(mediaId).subscribe((res) => {
      this.mediaRes = res;

      if (isDefined(res.body) && isDefined(res.body.type)) {
        this.readHeaders(res);
        // image/tiff
        if (this.contentType === MediaType.TIF || this.contentType === MediaType.TIFF) {
          this.subs.push(this.mediaService.loadContentForView(mediaId).subscribe((safeUrl) => {
            this.mediaUrl = safeUrl;
            this.contentType = MediaType.PDF;
            this.mediaLoaded.next(true);
          }));
        } else {
          // other media types
          const reader = new FileReader();
          reader.readAsDataURL(res.body);
          reader.onloadend = (data) => {
            this.mediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(String(data.target!.result));

            if (this.contentType === MediaType.XML && this.mediaUrl) {
              this.mediaService.formatXml(this.mediaUrl).then((xml) => this.formattedXml = xml);
            } else if (this.contentType === MediaType.CSV && this.mediaUrl) {
              this.mediaService.formatCsv(this.mediaUrl).then((csv) => this.formattedCsv = csv);
            }
            this.mediaLoaded.next(true);
          };
        }
      }
    }));
  }

  private readHeaders(res: HttpResponse<Blob>): void {
    if (isDefined(res.headers)) {
      const type: string | null = res.headers.get('Acc-Media-File-Type');
      if (isDefined(type) && nonBlank(type)) {
        this.mediaFileType = MediaFileType[type as keyof typeof MediaFileType];
        if (notDefined(this.mediaFileType)) {
          this.log.warn('Media File Type "{}" is missing in MediaViewer', [type]);
        }
      } else {
        this.mediaFileType = undefined;
      }
      const belong: string | null = res.headers.get('Acc-Media-Belong');
      if (isDefined(belong) && nonBlank(belong)) {
        this.mediaBelong = Belong[belong as keyof typeof Belong];
        if (notDefined(this.mediaBelong)) {
          this.log.warn('Media Belong "{}" is missing in MediaViewer', [belong]);
        }
      }
      const tags: string | null = res.headers.get('Acc-Media-Tags');
      this.mediaTags = isDefined(tags) && nonBlank(tags) ? tags : undefined;
      const deletableAfter: string | null = res.headers.get('Acc-Media-Deletable-After');
      this.mediaDeletableAfter = isDefined(deletableAfter) && nonBlank(deletableAfter) ? new Date(deletableAfter) : undefined;
    }
    if (isDefined(res.body) && isDefined(res.body.type)) {
      this.contentType = res.body.type;
      if (!this.supportedContentTypes.includes(this.contentType)) {
        this.log.error('Media Content Type "{}" is not supported in MediaViewer', [this.contentType]);
      }
    }
  }

  protected getTagsWrapper(): FormWrapper {
    if (notDefined(this.tagsWrapper)) {
      this.tagsWrapper = this.formService.buildWrapper('media-viewer-tags');
      this.tagsWrapper.build({
        tags: {value: this.mediaTags, disabled: !this.hasPermission(PermissionGrant.RW)},
      }, [
        new FormMetadata('tags').hideLabel()
      ]);
      this.tagsWrapper.onSubmitFn((v: any) => {
        return of(v);
      });
    }
    return this.tagsWrapper;
  }
}
